import { useEffect } from "react";
import { matchPath, useLocation } from "react-router";
import { atom } from "nanostores";

import { useStore } from "@nanostores/react";

export const MAX_HISTORY_LENGTH = 20;

const initialHistory: string[] = [];

export const $routeHistory = atom<string[]>(initialHistory);

export const addPathToHistory = (path: string) => {
  const currentHistory = $routeHistory.get();
  let newHistory = [...currentHistory];

  // Avoid adding consecutive duplicate routes
  if (newHistory.length === 0 || newHistory[newHistory.length - 1] !== path) {
    newHistory.push(path);
  }

  if (newHistory.length > MAX_HISTORY_LENGTH) {
    newHistory = newHistory.slice(-MAX_HISTORY_LENGTH);
  }

  $routeHistory.set(newHistory);
};

export const getRouteHistory = () => $routeHistory.get();

export const clearRouteHistory = () => {
  $routeHistory.set([]);
};

export const findMostRecentPathMatching = (
  predicate: (path: string) => boolean,
): string | undefined => {
  const history = $routeHistory.get();

  for (let i = history.length - 1; i >= 0; i--) {
    const historyPath = history[i];

    if (predicate(historyPath)) {
      return historyPath;
    }
  }

  return undefined;
};

export const useRouteHistory = () => {
  const history = useStore($routeHistory);
  const matchLastPath = (path: string) => {
    const lastPath = history[history.length - 1];
    // Extract just the pathname for matching (ignore query params)
    const lastPathname = lastPath ? lastPath.split("?")[0] : "";

    return matchPath(lastPathname, path);
  };

  const findMostRecentMatching = (predicate: (path: string) => boolean) =>
    findMostRecentPathMatching(predicate);

  const previousPath =
    history.length >= 2 ? history[history.length - 2] : undefined;

  return {
    history,
    matchLastPath,
    lastPath: previousPath || "",
    findMostRecentMatching,
  };
};

export const TrackRouteHistory = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname) {
      // Save the full path including query parameters
      const fullPath = location.pathname + location.search;
      addPathToHistory(fullPath);
    }
  }, [location.pathname, location.search]);

  return null;
};
